import React from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Skeleton from "@mui/material/Skeleton";
import { grey, red } from "@mui/material/colors";

import { InLakhsDisplay } from "../../../ReusableComponents/InLakhsDisplay";
import GrowthPercentage from "../../../ReusableComponents/GrowthPercentage";
import { tableBox, historyHeaderCellSx, textInfo } from "../storeBpTheme";
import { round1dp } from "../../data";
import { convertValueInL, convertValueInThousands } from "../../../../utils/roundDecimalValue";

const formatDispatch = (qty) => {
	const n = Number(qty) || 0;
	if (n >= 100000) return convertValueInL(n, "string");
	if (n >= 1000) return convertValueInThousands(n, "string");
	return String(n);
};

const HeaderRow = () => (
	<Box
		display="flex"
		sx={{
			background: grey[100],
			border: `1px solid ${grey[300]}`,
			borderRadius: 2,
			overflow: "hidden",
		}}
	>
		<Box sx={{ ...tableBox, width: "160px" }}>
			<Typography sx={historyHeaderCellSx}>RANGE</Typography>
		</Box>
		<Box sx={{ ...tableBox, width: "160px" }}>
			<Typography sx={historyHeaderCellSx}>REV</Typography>
		</Box>
		<Box sx={tableBox}>
			<Typography sx={historyHeaderCellSx}>ASP</Typography>
		</Box>
		<Box sx={tableBox}>
			<Typography sx={historyHeaderCellSx}>DIS</Typography>
		</Box>
		<Box sx={tableBox}>
			<Typography sx={historyHeaderCellSx}>ROS</Typography>
		</Box>
		<Box sx={tableBox}>
			<Typography sx={historyHeaderCellSx}>DISPATCH</Typography>
		</Box>
		<Box sx={tableBox}>
			<Typography sx={historyHeaderCellSx}>ST</Typography>
		</Box>
	</Box>
);

const growthPercent = (cur, base) => {
	if (!base) return 0;
	return round1dp(((cur - base) / base) * 100);
};

const compactGrowthSx = {
	flexShrink: 0,
	"& .MuiSvgIcon-root": { width: 12, height: 12, transform: "scale(1)" },
};
const compactGrowthText = { fontSize: "12px", whiteSpace: "nowrap" };

const RevWithGrowth = ({ revenue, growth }) => (
	<Box display="flex" alignItems="center" justifyContent="center" columnGap={1} sx={{ minWidth: 0 }}>
		<InLakhsDisplay number={revenue} sx={{ fontSize: 16, fontWeight: "bold", flexShrink: 0 }} />
		{growth != null && (
			<GrowthPercentage number={growth} textStyle={compactGrowthText} sx={compactGrowthSx} />
		)}
	</Box>
);

const HistoryRow = ({ label, kpi, growth, showDispatchSt = false }) => (
	<Box display="flex" alignItems="center">
		<Box sx={{ ...tableBox, width: "160px" }}>
			<Typography sx={{ ...textInfo, fontWeight: "bold", fontSize: 15 }}>{label}</Typography>
		</Box>
		<Box sx={{ ...tableBox, width: "160px" }}>
			{kpi ? <RevWithGrowth revenue={kpi.revenue} growth={growth} /> : <Skeleton width={60} height={26} />}
		</Box>
		<Box sx={tableBox}>{kpi ? <Typography sx={textInfo}>{kpi.asp}</Typography> : <Skeleton width={40} height={26} />}</Box>
		<Box sx={tableBox}>{kpi ? <Typography sx={textInfo}>{kpi.dis}%</Typography> : <Skeleton width={40} height={26} />}</Box>
		<Box sx={tableBox}>{kpi ? <Typography sx={textInfo}>{kpi.ros}</Typography> : <Skeleton width={40} height={26} />}</Box>
		<Box sx={tableBox}>
			{showDispatchSt && kpi ? (
				<Typography sx={textInfo}>{formatDispatch(kpi.dispatch)}</Typography>
			) : (
				<Typography sx={textInfo}>---</Typography>
			)}
		</Box>
		<Box sx={tableBox}>
			{showDispatchSt && kpi ? <Typography sx={textInfo}>{kpi.st}%</Typography> : <Typography sx={textInfo}>---</Typography>}
		</Box>
	</Box>
);

const SeasonInfoPanel = ({
	ltlKpi,
	lltlKpi,
	prevLltlKpi,
	sixMKpi,
	prvSixMKpi,
	targetData,
	derivedTarget,
	canEdit,
	onTargetFieldChange,
	onSave,
}) => {
	const lltlGrowth = lltlKpi && prevLltlKpi ? growthPercent(lltlKpi.revenue, prevLltlKpi.revenue) : null;
	const ltlGrowth = ltlKpi && lltlKpi ? growthPercent(ltlKpi.revenue, lltlKpi.revenue) : null;
	const sixMGrowth = sixMKpi && prvSixMKpi ? growthPercent(sixMKpi.revenue, prvSixMKpi.revenue) : null;

	const newTargetGrowth = ltlKpi
		? {
				revenue: growthPercent(targetData.revenue, ltlKpi.revenue),
				asp: growthPercent(targetData.asp, ltlKpi.asp),
				dis: growthPercent(targetData.dis, ltlKpi.dis),
			}
		: {};

	const derivedGrowth = {
		revenue: growthPercent(derivedTarget.revenue, targetData.revenue),
		asp: growthPercent(derivedTarget.asp, targetData.asp),
		dis: growthPercent(derivedTarget.dis, targetData.dis),
		ros: growthPercent(derivedTarget.ros, targetData.ros),
	};

	return (
		<Stack width="100%" flex={1} minWidth={0} spacing={1.5}>
			<HeaderRow />

			<HistoryRow label="LLTL AVG" kpi={lltlKpi} growth={lltlGrowth} showDispatchSt />
			<HistoryRow label="LTL AVG" kpi={ltlKpi} growth={ltlGrowth} showDispatchSt />
			<HistoryRow label="6 M AVG" kpi={sixMKpi} growth={sixMGrowth} />

			{/* NEW TARGET */}
			<Box
				display="flex"
				alignItems="center"
				sx={
					canEdit
						? { boxShadow: "1px 1px 4px lightgrey", border: `1px solid ${red[900]}`, padding: "7px 5px", borderRadius: "4px" }
						: { background: grey[200], boxShadow: "1px 1px 4px lightgrey", padding: "7px 5px" }
				}
			>
				<Box sx={{ ...tableBox, width: "160px" }}>
					<Typography sx={{ ...textInfo, fontWeight: "bold", fontSize: 15 }}>NEW TARGET</Typography>
				</Box>
				<Box sx={{ ...tableBox, width: "160px" }}>
					<RevWithGrowth revenue={targetData.revenue} growth={ltlKpi ? newTargetGrowth.revenue : null} />
				</Box>
				<Box sx={tableBox}>
					{canEdit ? (
						<TextField
							size="small"
							type="number"
							value={targetData.asp || ""}
							onChange={(e) => onTargetFieldChange("asp", e.target.value)}
							sx={{ width: 80 }}
							inputProps={{ style: { textAlign: "center" } }}
						/>
					) : (
						<Box display="flex" alignItems="center" columnGap={1}>
							<Typography sx={textInfo}>{targetData.asp || 0}</Typography>
							{ltlKpi && (
								<GrowthPercentage
									number={newTargetGrowth.asp}
									textStyle={compactGrowthText}
									sx={compactGrowthSx}
								/>
							)}
						</Box>
					)}
				</Box>
				<Box sx={tableBox}>
					{canEdit ? (
						<TextField
							size="small"
							type="number"
							InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
							value={targetData.dis || ""}
							onChange={(e) => onTargetFieldChange("dis", e.target.value)}
							sx={{ width: 90 }}
							inputProps={{ style: { textAlign: "center" } }}
						/>
					) : (
						<Box display="flex" alignItems="center" columnGap={1}>
							<Typography sx={textInfo}>{targetData.dis || 0}%</Typography>
							{ltlKpi && (
								<GrowthPercentage
									number={newTargetGrowth.dis}
									textStyle={compactGrowthText}
									sx={compactGrowthSx}
								/>
							)}
						</Box>
					)}
				</Box>
				<Box sx={tableBox}>
					<Typography sx={textInfo}>---</Typography>
				</Box>
				<Box sx={tableBox}>
					<Typography sx={textInfo}>---</Typography>
				</Box>
				<Box sx={tableBox}>
					<Typography sx={textInfo}>---</Typography>
				</Box>
			</Box>

			{/* DERIVED TARGET */}
			<Box display="flex" alignItems="center" sx={{ background: grey[50], padding: "7px 5px", borderRadius: "4px" }}>
				<Box sx={{ ...tableBox, width: "160px" }}>
					<Typography sx={{ ...textInfo, fontWeight: "bold", fontSize: 15 }}>DERIVED TARGET</Typography>
				</Box>
				<Box sx={{ ...tableBox, width: "160px" }}>
					<RevWithGrowth revenue={derivedTarget.revenue} growth={derivedGrowth.revenue} />
				</Box>
				<Box sx={tableBox}>
					<Typography sx={textInfo}>{derivedTarget.asp || 0}</Typography>
				</Box>
				<Box sx={tableBox}>
					<Typography sx={textInfo}>{derivedTarget.dis || 0}%</Typography>
				</Box>
				<Box sx={tableBox}>
					<Typography sx={textInfo}>{derivedTarget.ros || 0}</Typography>
				</Box>
				<Box sx={tableBox}>
					<Typography sx={textInfo}>---</Typography>
				</Box>
				<Box sx={tableBox}>
					<Typography sx={textInfo}>---</Typography>
				</Box>
			</Box>

			{/* Derived vs New */}
			<Box display="flex" alignItems="center">
				<Box sx={{ ...tableBox, width: "160px" }}>
					<Typography sx={{ ...textInfo, fontWeight: "bold", fontSize: 14 }}>Derived vs New</Typography>
				</Box>
				<Box sx={{ ...tableBox, width: "160px" }}>
					<GrowthPercentage number={derivedGrowth.revenue} textStyle={compactGrowthText} sx={compactGrowthSx} />
				</Box>
				<Box sx={tableBox}>
					<GrowthPercentage number={derivedGrowth.asp} textStyle={compactGrowthText} sx={compactGrowthSx} />
				</Box>
				<Box sx={tableBox}>
					<GrowthPercentage number={derivedGrowth.dis} textStyle={compactGrowthText} sx={compactGrowthSx} />
				</Box>
				<Box sx={tableBox}>
					<GrowthPercentage number={derivedGrowth.ros} textStyle={compactGrowthText} sx={compactGrowthSx} />
				</Box>
				<Box sx={tableBox}>
					<Typography sx={textInfo}>---</Typography>
				</Box>
				<Box sx={tableBox}>
					<Typography sx={textInfo}>---</Typography>
				</Box>
			</Box>

			{canEdit && (
				<Box pt={1.5} width="100%" display="flex" justifyContent="flex-end" columnGap={2}>
					<Button variant="contained" sx={{ width: "180px", textTransform: "capitalize" }} onClick={onSave}>
						Save Target
					</Button>
				</Box>
			)}
		</Stack>
	);
};

export default SeasonInfoPanel;
